import React, { useEffect, useRef, useState } from 'react';
import { IconButton, CircularProgress, Box, Typography } from '@mui/material';
import {
  WarningRounded as WarningRoundedIcon,
  HistoryToggleOffRounded as HistoryToggleOffRoundedIcon,
  CheckRounded as CheckRoundedIcon,
  SlowMotionVideoRounded as SlowMotionVideoRoundedIcon,
  PlayArrowRounded as PlayArrowRoundedIcon,
  PauseRounded as PauseRoundedIcon,
} from '@mui/icons-material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import sanitize from 'sanitize-html';
import { CONVERTING_STATUS } from '@src/constants/project';
import Tooltip from '@src/components/Tooltip';
import actions from '@src/redux/actions';
import useUserInfo from '@src/hooks/useTtsUser';
import { COLOR } from '@src/styles/color';
import { countTextLength } from '@src/services/tts';
import BlockAudioPopover from './BlockAudioPopover';

const NewSynthesisButton = ({
  isDisableConvertBtn,
  // A lock the escape hatches below cannot lift: another operation is holding
  // this block. Defaults to false so callers that don't have one are unchanged.
  isConvertLocked = false,
  status,
  isPlaying,
  blockId,
  showSuccessIcon,
  audioExpiredAt,
  checkValidCreateSynthesis,
  handlePlayAudio,
  isDisablePlayAudio,
  onConvertBlockToAudio,
  isBlockEditting,
  isDubbingProject,
  enableCreditUsagePreview,
  isBlockUIImprovement,
  // AIV-12773 US-14: opt-in to show the single-block convert button for video
  // dubbing (kept off by default so basic SRT dubbing behaviour is unchanged).
  enableDubbingBlockAudio,
  showAudioPopover,
  audioLink,
  popoverCurrentTime,
  popoverDuration,
  onSeekInBlock,
  // The text this block's synthesis will read. Omitted by callers whose editor
  // writes `block.text`; passed by one whose editor writes another field, so the
  // price the button shows and the text it converts stay the same string.
  synthesisText,
}) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;

  const dispatch = useDispatch();
  // Only THIS block, not the whole map: the button reads nothing else from it,
  // so subscribing to `multiBlock` (or the whole slice) meant every block's
  // button re-rendered whenever any single block was edited.
  const block = useSelector(
    (state) => state.project.multiBlock?.blocks?.[blockId],
  );
  const voiceDefault = useSelector((state) => state.project.voiceDefault);
  const isGeneratingNarration = useSelector(
    (state) => state.project.isGeneratingNarration,
  );
  const generatingBlockIds = useSelector(
    (state) => state.project.generatingBlockIds,
  );
  const isGeneratingThisBlock =
    isGeneratingNarration && generatingBlockIds?.includes(blockId);
  const { ttsUser } = useUserInfo();
  const userAudioRetentionPeriod = ttsUser.retentionPeriod;
  const isInitialMount = useRef(true);

  const isSynthesisInProgress = status === CONVERTING_STATUS.IN_PROGRESS;
  const isSynthesisSuccess = status === CONVERTING_STATUS.SUCCESS;
  const isSynthesisFailure = status === CONVERTING_STATUS.FAILURE;
  const isAudioExpired = audioExpiredAt && moment().isAfter(audioExpiredAt);
  // `isAudioExpired` and `isSynthesisFailure` are escape hatches over a parent
  // that says "no need to convert". They must not reopen a parent that says
  // "another operation owns this block", which is what `isConvertLocked` carries.
  const canConvertBlock =
    (isAudioExpired || isSynthesisFailure || !isDisableConvertBtn) &&
    !isConvertLocked;
  const isEnableConvertBtn = canConvertBlock && !isGeneratingNarration;

  const [tempSuccessIcon, setTempSuccessIcon] = useState(false);

  // Priced on the text the synthesis will read, which is not always
  // `block.text`: a caller editing a translation passes the field it writes.
  const costsCredit =
    (block?.voice?.creditFactor || voiceDefault?.creditFactor) *
    countTextLength(synthesisText ?? block?.text ?? '');

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (showSuccessIcon) {
      setTempSuccessIcon(true);
      setTimeout(() => {
        setTempSuccessIcon(false);
      }, 3000);
    }
  }, [showSuccessIcon]);

  const handleConvertBlockToAudio = async () => {
    if (!isEnableConvertBtn) return;
    if (!checkValidCreateSynthesis(block)) return;
    onConvertBlockToAudio(block);
  };
  const handlePlayAudioInBlock = () => {
    if (handlePlayAudio) handlePlayAudio(blockId, audioExpiredAt);
  };

  const handleStopAudio = () => {
    dispatch(actions.project.setIsPlayingBlock(false));
  };

  const renderSynthesisStatusIcon = () => {
    if (isAudioExpired && isSynthesisSuccess) {
      return (
        <Tooltip
          arrow
          title={
            <Typography
              component="span"
              sx={{ fontSize: '14px', textAlign: 'left' }}
              style={{ textAlign: 'left' }}
              dangerouslySetInnerHTML={{
                __html: sanitize(
                  t('blockAudioExpired', {
                    audioRetentionPeriod: userAudioRetentionPeriod,
                  }),
                  {
                    allowedTags: ['span'],
                    allowedAttributes: {
                      span: ['style'],
                    },
                  },
                ),
              }}
            />
          }
          placement="left"
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            className="exceed-block-char-icon"
          >
            <HistoryToggleOffRoundedIcon
              className="icon"
              sx={{ color: COLOR.redPrimary }}
            />
          </div>
        </Tooltip>
      );
    }

    if (isSynthesisFailure) {
      return (
        <Tooltip
          maxWidth="100%"
          arrow
          title={t('blockSynthesisFailure')}
          placement="left"
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            className="exceed-block-char-icon"
          >
            <WarningRoundedIcon
              sx={{ color: COLOR.danger, fontSize: '24px' }}
            />
          </div>
        </Tooltip>
      );
    }

    return null;
  };

  const renderPlayOrPauseAudioButton = () => {
    if (isDubbingProject && !enableDubbingBlockAudio) return null;
    if (isGeneratingThisBlock) return null;
    if (isBlockUIImprovement) {
      if ((!isSynthesisSuccess && !isSynthesisFailure) || isDisablePlayAudio)
        return null;
      if (tempSuccessIcon) return null;
    }
    if (isPlaying) {
      return (
        <Tooltip
          arrow
          title={t('pause')}
          disableHoverListener={isBlockUIImprovement}
          placement="top"
        >
          <IconButton onClick={handleStopAudio} className="editor-toolbar-btn">
            <PauseRoundedIcon />
          </IconButton>
        </Tooltip>
      );
    }
    return (
      <Tooltip
        sx={{ display: isDisablePlayAudio ? 'none' : 'block' }}
        arrow
        disableHoverListener={isBlockUIImprovement}
        title={t('playAudio')}
        placement="top"
      >
        <div className={isDisablePlayAudio ? 'disabled-btn' : ''}>
          <IconButton
            onClick={handlePlayAudioInBlock}
            className="editor-toolbar-btn"
            disabled={isDisablePlayAudio}
          >
            <PlayArrowRoundedIcon />
          </IconButton>
        </div>
      </Tooltip>
    );
  };

  const renderSynthesisButton = () => {
    if (isDubbingProject && !enableDubbingBlockAudio) {
      // In basic SRT dubbing project mode, hide single-block convert button
      return null;
    }
    if (isSynthesisInProgress) {
      return (
        <Box className="convert-progress">
          <div className="inprogress-icon-container">
            <CircularProgress size={20} className="inprogress-icon" />
          </div>
        </Box>
      );
    }
    if (tempSuccessIcon) {
      return (
        <div className="success-icon">
          <CheckRoundedIcon sx={{ color: '#007AFF' }} />
        </div>
      );
    }
    // FF ON: ẩn Convert nếu block đã từng synthesis (Play hiện ở renderPlayOrPauseAudioButton)
    if (
      isBlockUIImprovement &&
      (isSynthesisSuccess || isSynthesisFailure) &&
      !isDisablePlayAudio
    ) {
      return null;
    }
    return (
      <Tooltip
        sx={{ display: canConvertBlock ? 'block' : 'none' }}
        arrow
        title={
          enableCreditUsagePreview
            ? t('costsCredit', { value: costsCredit?.toLocaleString(language) })
            : t('convertToAudioToListen')
        }
        placement="top"
      >
        <div className={isEnableConvertBtn ? '' : 'disabled-btn'}>
          <IconButton
            onClick={handleConvertBlockToAudio}
            className="editor-toolbar-btn"
            disabled={!isEnableConvertBtn}
          >
            <SlowMotionVideoRoundedIcon />
          </IconButton>
        </div>
      </Tooltip>
    );
  };

  return (
    <Box className="convert-progress">
      {isBlockEditting ? null : renderSynthesisStatusIcon()}
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '12px',
          position: 'relative',
        }}
      >
        {showAudioPopover && (
          <BlockAudioPopover
            open={isPlaying}
            audioLink={audioLink}
            currentTime={popoverCurrentTime}
            duration={popoverDuration}
            onSeek={onSeekInBlock}
          />
        )}
        {renderPlayOrPauseAudioButton()}
      </div>
      {renderSynthesisButton()}
    </Box>
  );
};

export default NewSynthesisButton;
